// English source strings for the people & records stream (module 03 remainders, ADR-023,
// PD-290 – PD-309): watchlist screening, sensitive-profile masking, field dictionaries, host
// directory synchronisation and record import.
export const enPeople = {
  // ------------------------------------------------------------ navigation, roles, reception
  'nav.screening': 'Screening',
  'specialist.screening-list-manager': 'Screening list manager',
  'specialist.restricted-profile-reader': 'Restricted profile reader',
  // Visitor-safe screening facts: never the list, entry or reason (PD-296).
  'rcp.blocker.security_review': 'A security review is required before arrival. Ask a security supervisor; do not discuss the reason with the visitor.',
  'rcp.blocker.screening_unavailable': 'The security check could not be completed or is out of date. Run the check again.',
  'rcp.warning.security_review': 'A security review is open for this visitor. Inform a security supervisor.',
  'rcp.warning.screening_unavailable': 'The security check could not be completed or is out of date.',
  'ppl.rcp.runCheck': 'Run security check again',
  'ppl.rcp.checkDone': 'Security check recorded.',

  // ------------------------------------------------------------ API and validation messages
  'ppl.api.revealExpired': 'This reveal has expired. Reveal again, stating the purpose.',
  'ppl.api.hostSyncDisabled': 'Host synchronisation is not enabled for this directory connection.',
  'ppl.api.hostAlreadyLinked': 'This host is already linked to a directory.',
  'ppl.api.providerListReadOnly': 'Entries of a provider list come from the provider and cannot be edited here.',
  'ppl.api.entryRemoved': 'This entry has already been removed.',
  'ppl.api.participantRemoved': 'This person was removed from the visit.',
  'ppl.api.caseDecided': 'This case has already been decided.',
  'ppl.api.ownVisitCase': 'You host or created this visit, so another reviewer must decide this case.',
  'ppl.api.importOpen': 'This file is already being imported. Continue or discard that import first.',
  'ppl.api.importNotEditable': 'This import can no longer be changed.',
  'ppl.api.importRowNotDecidable': 'Only rows that are valid or possible duplicates take a decision.',
  'ppl.api.importPolicyFixed': 'This import is already being applied with another policy.',
  'ppl.api.importHasProblems': 'Some rows are invalid or still need a duplicate decision. Fix them, or choose “apply valid rows only”.',
  'ppl.api.importChanged': 'A row could not be applied because records changed since the dry run. Nothing was applied. Run the import again.',
  'ppl.v.reason': 'Give a reason (at least 3 characters).',
  'ppl.v.fieldKey': 'Use lower-case letters, digits and underscores, starting with a letter.',
  'ppl.v.option': 'Choose one of the listed options.',
  'ppl.v.country': 'Use a two-letter country code (for example AE).',
  'ppl.v.formula': 'Values must not start with =, +, - or @ (spreadsheet formula protection).',
  'ppl.v.fieldUnknown': 'This field is not configured for this record or category.',
  'ppl.v.purpose': 'Choose one of the configured purposes.',
  'ppl.v.externalIdTaken': 'Another host already uses this directory identifier.',
  'ppl.v.hostDirectoryOwned': 'This value comes from the employee directory and is changed there.',
  'ppl.v.simulatorProduction': 'The screening simulator cannot be used in production.',
  'ppl.v.validUntil': 'The end date cannot be before the start date.',
  'ppl.v.reviewerUnknown': 'Choose a user who may review screening cases at this site.',
  'ppl.v.locale': 'Use en or ar.',
  'ppl.v.employeeNumber': 'Use 1–50 letters, digits, dots, underscores or hyphens.',
  'ppl.v.siteCode': 'No site has this code.',
  'ppl.v.orgKind': 'Use visitor, contractor, vendor, delivery, government or other.',
  'ppl.v.changedSinceValidation': 'Records changed since the dry run; this row was not applied.',
  'ppl.v.headerUnknown': 'This column is not in the file.',
  'ppl.v.headerBlocked': 'Credential and secret columns are never imported.',
  'ppl.v.fieldMappedTwice': 'Each field can be mapped from one column only.',
  'ppl.v.requiredColumn': 'Map a column to this required field.',
  'ppl.v.matchUnknown': 'Choose one of the listed possible matches.',
  'ppl.v.noMerge': 'People and organisations are never merged; use the existing record or create a separate one.',
  'ppl.v.dict.duplicate': 'This key is used twice.',
  'ppl.v.dict.reserved': 'This key is a built-in field.',
  'ppl.v.dict.forbidden': 'Credential or secret fields cannot be configured.',
  'ppl.v.dict.options': 'Add at least one option.',
  'ppl.v.dict.range': 'The minimum is above the maximum.',
  'ppl.v.dict.purpose': 'This purpose code is used twice.',
  'ppl.v.dict.category': 'Unknown visitor category.',
  'ppl.import.p.empty': 'The file has no rows.',
  'ppl.import.p.tooLarge': 'The file is too large.',
  'ppl.import.p.notUtf8': 'CSV files must be UTF-8 encoded.',
  'ppl.import.p.binary': 'The file is not a text CSV file.',
  'ppl.import.p.quote': 'A quoted value is not closed.',
  'ppl.import.p.tooManyRows': 'The file has too many rows for one import.',
  'ppl.import.p.tooManyColumns': 'The file has too many columns.',
  'ppl.import.p.notXlsx': 'The file is not a readable XLSX workbook.',
  'ppl.import.p.macro': 'Macro-enabled workbooks are not accepted.',
  'ppl.import.p.blankHeader': 'Every column needs a header in the first row.',
  'ppl.import.p.duplicateHeader': 'Two columns have the same header.',
  'ppl.import.p.encoding': 'The file could not be read.',
  'ppl.import.f.givenName': 'Given name',
  'ppl.import.f.familyName': 'Family name',
  'ppl.import.f.email': 'Email',
  'ppl.import.f.phone': 'Mobile',
  'ppl.import.f.organisation': 'Organisation',
  'ppl.import.f.preferredLocale': 'Language (en/ar)',
  'ppl.import.f.displayName': 'Name',
  'ppl.import.f.employeeNumber': 'Employee number',
  'ppl.import.f.department': 'Department',
  'ppl.import.f.jobTitle': 'Job title',
  'ppl.import.f.officeSiteCode': 'Office site code',
  'ppl.import.f.name': 'Organisation name',
  'ppl.import.f.kind': 'Type',
  // ------------------------------------------------------------ tabs and shared labels
  'ppl.tab.hostSync': 'Host sync',
  'ppl.tab.import': 'Import',
  'ppl.tab.fields': 'Field dictionary',
  'ppl.class.standard': 'Standard',
  'ppl.class.contact': 'Contact',
  'ppl.class.identity': 'Identity',
  'ppl.class.restricted': 'Restricted',
  'ppl.class.standard.hint': 'Shown to everyone who can see the profile.',
  'ppl.class.contact.hint': 'Shown in full to hosts and reception; masked for approvers.',
  'ppl.class.identity.hint': 'Always masked; identity verifiers reveal it for a stated purpose.',
  'ppl.class.restricted.hint': 'Always masked; restricted profile readers reveal it for a stated purpose.',
  'ppl.type.text': 'Text',
  'ppl.type.number': 'Number',
  'ppl.type.date': 'Date',
  'ppl.type.boolean': 'Yes / no',
  'ppl.type.select': 'Choice',
  'ppl.type.email': 'Email',
  'ppl.type.phone': 'Phone',
  'ppl.type.country': 'Country code',
  'ppl.collect.none': 'Not collected',
  'ppl.collect.all': 'Every category',
  'ppl.collect.categories': 'Listed categories',

  // ------------------------------------------------------------ profile fields and reveals (PB-03-07)
  'ppl.fields.title': 'Profile details',
  'ppl.fields.sub': 'Configured fields. Identity and restricted values stay masked unless your role allows a reveal for a stated purpose.',
  'ppl.fields.edit': 'Edit details',
  'ppl.fields.editSub': 'Only changed fields are saved. Identity and restricted values are typed in full and shown masked afterwards.',
  'ppl.fields.history': 'Access history',
  'ppl.fields.none': 'No configured details for this person.',
  'ppl.fields.noneHost': 'No host fields are configured in the field dictionary.',
  'ppl.fields.hostTitle': 'Host details · {name}',
  'ppl.fields.masked': 'Masked value',
  'ppl.fields.reveal': 'Reveal',
  'ppl.fields.hide': 'Hide now',
  'ppl.fields.revealedFor': 'Revealed values are shown for {seconds} more seconds.',
  'ppl.fields.maskNote': 'Revealing is recorded with your name, the purpose and the time. Values are never kept on this device.',
  'ppl.fields.writeOnly': 'Current value {masked}. Leave empty to keep it.',
  'ppl.reveal.title': 'Reveal masked details',
  'ppl.reveal.sub': 'The values are shown for {seconds} seconds and the reveal is recorded.',
  'ppl.reveal.purpose': 'Purpose',
  'ppl.reveal.note': 'Note (optional)',
  'ppl.reveal.noteHint': 'For example a desk or case reference.',
  'ppl.reveal.audited': 'Your name, the purpose, the fields and the time are recorded in the access history.',
  'ppl.history.none': 'No reveals recorded.',
  'ppl.history.when': 'When',
  'ppl.history.who': 'Who',
  'ppl.history.fields': 'Fields',

  // ------------------------------------------------------------ host directory additions (PB-03-02, OR-08)
  'ppl.hosts.fields': 'Details',
  'ppl.hosts.deactivateReassign': 'Deactivate and route these visits to reassignment',
  'ppl.hosts.reassignReason': 'Host deactivated; future visits routed to reassignment',

  // ------------------------------------------------------------ visitor policy (PB-03-06)
  'ppl.policy.screening': 'Watchlist screening',
  'ppl.policy.screeningHint': 'A possible match is held for a security supervisor to review; it is never an automatic refusal and the reason is never shown to reception, hosts or visitors.',
  'ppl.policy.mode': 'Possible match',
  'ppl.policy.mode.off': 'Not screened',
  'ppl.policy.mode.warn': 'Warn reception',
  'ppl.policy.mode.block': 'Hold arrival until reviewed',
  'ppl.policy.unavailable': 'Check unavailable or out of date',

  // ------------------------------------------------------------ screening workspace (PB-03-06)
  'ppl.scr.eyebrow': 'Security',
  'ppl.scr.title': 'Screening',
  'ppl.scr.intro': 'Possible watchlist and deny-list matches waiting for review, and the lists they come from.',
  'ppl.scr.restricted': 'Restricted information',
  'ppl.scr.restrictedNote': 'Lists, matches and reasons are visible only to named security staff. Never share them with visitors, hosts or reception; a match is a reason to review, not to refuse.',
  'ppl.scr.tab.cases': 'Cases',
  'ppl.scr.tab.lists': 'Lists',
  'ppl.scr.tab.settings': 'Settings',
  'ppl.scr.cases': 'Cases · {site}',
  'ppl.scr.casesSub': 'One case per visitor and visit. Decide each with a reason.',
  'ppl.scr.status': 'Status',
  'ppl.scr.otherSite': 'You do not review screening cases for the working site. Choose another site.',
  'ppl.scr.noCases': 'No cases here.',
  'ppl.scr.col.person': 'Person',
  'ppl.scr.col.visit': 'Visit',
  'ppl.scr.col.score': 'Match score',
  'ppl.scr.col.status': 'Status',
  'ppl.scr.col.reviewer': 'Reviewer',
  'ppl.scr.opened': 'Opened {when}',
  'ppl.scr.candidates': { one: 'One possible match', other: '{count} possible matches' },
  'ppl.scr.unassigned': 'Unassigned',
  'ppl.scr.review': 'Review',
  'ppl.scr.listsSub': 'Lists you manage. Organisation-wide lists need an organisation-wide grant.',
  'ppl.case.status.open': 'Awaiting review',
  'ppl.case.status.cleared': 'Cleared',
  'ppl.case.status.confirmed': 'Confirmed match',
  'ppl.case.status.all': 'All',
  'ppl.case.title': 'Screening case',
  'ppl.case.sub': 'Compare the visitor with each candidate before deciding.',
  'ppl.case.candidates': 'Candidates',
  'ppl.case.candidatesNote': 'Scores come from fuzzy name matching across Arabic and Latin spellings; they are not identity proof.',
  'ppl.case.list': 'List',
  'ppl.case.matched': 'Matched name',
  'ppl.case.reason': 'Listing reason',
  'ppl.case.previous': 'Earlier cases for this person',
  'ppl.case.disposition': 'Decision',
  'ppl.case.reasonLabel': 'Reason for the decision',
  'ppl.case.reasonHint': 'Required. Kept with the case; never shown outside security review.',
  'ppl.case.note': 'Restricted note (optional)',
  'ppl.case.noteHint': 'Visible only to screening reviewers.',
  'ppl.case.decide': 'Record decision',
  'ppl.case.events': 'Case history',
  'ppl.disposition.false_positive': 'Not the listed person (false positive)',
  'ppl.disposition.not_relevant': 'Listed person, may proceed',
  'ppl.disposition.confirmed_match': 'Confirmed match — keep refused',
  'ppl.disposition.false_positive.hint': 'Clears the hold. The same person is not flagged again for these unchanged entries.',
  'ppl.disposition.not_relevant.hint': 'Clears the hold for this visit only, for example with an escort agreed.',
  'ppl.disposition.confirmed_match.hint': 'Arrival stays blocked for this visit. Reception sees only that a security review is required.',
  'ppl.event.opened': 'Opened',
  'ppl.event.candidates_added': 'New candidates added',
  'ppl.event.assigned': 'Assigned',
  'ppl.event.decided': 'Decided',
  'ppl.event.note': 'Note',
  'ppl.match.name_exact': 'same name',
  'ppl.match.name_fuzzy': 'similar spelling',
  'ppl.match.name_transliterated': 'Arabic/Latin transliteration',
  'ppl.match.alias': 'alias',
  'ppl.match.dob_match': 'same date of birth',
  'ppl.match.dob_mismatch': 'different date of birth',
  'ppl.match.identifier': 'same contact or document',
  'ppl.reason.safety_incident': 'Safety incident',
  'ppl.reason.trespass': 'Trespass',
  'ppl.reason.legal_order': 'Legal order',
  'ppl.reason.policy_violation': 'Policy violation',
  'ppl.reason.provider_listed': 'Listed by provider',
  'ppl.reason.other': 'Other',
  'ppl.list.add': 'Add list',
  'ppl.list.addSub': 'Record where the list comes from and under which authority.',
  'ppl.list.none': 'No lists yet.',
  'ppl.list.name': 'Name',
  'ppl.list.scope': 'Applies at',
  'ppl.list.kind': 'Type',
  'ppl.list.kind.deny': 'Deny list',
  'ppl.list.kind.watch': 'Watchlist',
  'ppl.list.source': 'Source',
  'ppl.list.source.manual': 'Kept in Passage',
  'ppl.list.source.provider': 'Screening provider',
  'ppl.list.providerHint': 'No provider is contracted yet; only the development simulator is available (refused in production).',
  'ppl.list.provenance': 'Provenance',
  'ppl.list.provenanceHint': 'Who maintains the list and under which authority.',
  'ppl.list.entries': 'Entries',
  'ppl.list.providerEntries': 'From provider',
  'ppl.list.changed': 'Changed {when}',
  'ppl.list.manageEntries': 'Entries',
  'ppl.list.activate': 'Activate',
  'ppl.list.deactivate': 'Deactivate',
  'ppl.list.activateSub': 'Upcoming visits are screened again against this list.',
  'ppl.list.deactivateSub': 'The list stops being used for new checks. Open cases stay open.',
  'ppl.entry.sub': 'Restricted entries. Opening this list is recorded.',
  'ppl.entry.none': 'No entries.',
  'ppl.entry.add': 'Add entry',
  'ppl.entry.addSub': 'Upcoming visits are screened again after you save.',
  'ppl.entry.name': 'Full name',
  'ppl.entry.nameHint': 'Arabic or Latin script; both are matched.',
  'ppl.entry.aliases': 'Other spellings',
  'ppl.entry.aliasesHint': 'One per line.',
  'ppl.entry.dob': 'Date of birth',
  'ppl.entry.idKind': 'Identifier type',
  'ppl.entry.idValue': 'Identifier',
  'ppl.entry.id.email': 'Email',
  'ppl.entry.id.phone': 'Phone',
  'ppl.entry.id.document': 'Document number',
  'ppl.entry.validFrom': 'Valid from',
  'ppl.entry.validUntil': 'Valid until',
  'ppl.entry.sourceRef': 'Source reference',
  'ppl.entry.noteHint': 'Visible only to screening reviewers and list managers.',
  'ppl.entry.remove': 'Remove',
  'ppl.entry.status.active': 'Active',
  'ppl.entry.status.removed': 'Removed',
  'ppl.set.sub': 'Organisation-wide screening behaviour. Values are development defaults until reviewed.',
  'ppl.set.threshold': 'Match threshold (0.6–1)',
  'ppl.set.suppressionDays': 'Remember cleared false positives (days)',
  'ppl.set.providerTimeoutMs': 'Provider timeout (ms)',
  'ppl.set.rescreenHorizonDays': 'Re-screen visits within (days)',
  'ppl.set.rescreenMax': 'Re-screen at most (visitors per change)',
  'ppl.set.note': 'A provider timeout is treated as unavailable, never as clear.',

  // ------------------------------------------------------------ host sync (PB-03-09)
  'ppl.hs.intro': 'Keep the host directory in step with the employee directory.',
  'ppl.hs.title': 'Host directory synchronisation',
  'ppl.hs.note': 'Hosts are matched by the directory’s permanent identifier, never by email. Hosts are never deleted: when someone leaves the directory their host profile becomes inactive and their future visits wait for reassignment.',
  'ppl.hs.none': 'No directory connection exists yet.',
  'ppl.hs.toIdentity': 'Connect a directory in Identity providers',
  'ppl.hs.enabled': 'Host sync on',
  'ppl.hs.disabled': 'Host sync off',
  'ppl.hs.configure': 'Configure',
  'ppl.hs.run': 'Synchronise hosts now',
  'ppl.hs.confirm': 'Apply the held deactivations',
  'ppl.hs.stale': 'The directory could not be read on the last run. Host details may be out of date.',
  'ppl.hs.owned': 'Hosts from this directory',
  'ppl.hs.inactive': 'Inactive',
  'ppl.hs.mapping': 'Attribute mapping',
  'ppl.hs.lastRun': 'Last run',
  'ppl.hs.result': '{created} created, {updated} updated, {deactivated} deactivated, {reactivated} reactivated, {unchanged} unchanged, {conflicts} conflicts, {reassignments} visits to reassign.',
  'ppl.hs.enable': 'Synchronise hosts after every directory run',
  'ppl.hs.linkAccounts': 'Link each host to the sign-in account from the same directory entry',
  'ppl.hs.mappingHint': 'Directory attribute names. Leave empty to keep the field managed in Passage.',
  'ppl.hs.f.department': 'Department',
  'ppl.hs.f.jobTitle': 'Job title',
  'ppl.hs.f.employeeNumber': 'Employee number',
  'ppl.hs.f.officeSiteCode': 'Office site code',
  'ppl.hs.outcome.succeeded': 'Completed',
  'ppl.hs.outcome.held': 'Held for confirmation',
  'ppl.hs.outcome.failed': 'Directory unavailable',
  'ppl.hs.conflict.host_exists': 'A host with this email already exists; link it explicitly if it is the same person',
  'ppl.hs.conflict.employee_number_in_use': 'Employee number used by another host',
  'ppl.hs.conflict.duplicate_external_id': 'Directory identifier appears twice or belongs to another source',
  'ppl.hs.conflict.invalid_entry': 'Invalid directory entry',
  'ppl.hs.conflict.office_site_unknown': 'Unknown office site code',
  'ppl.hs.conflict.deactivation_threshold': 'Unusually many deactivations — held until confirmed',
  'ppl.hs.conflict.administrator_hold': 'Deactivated by an administrator; not reactivated automatically',
  'ppl.hs.conflict.account_linked_elsewhere': 'The sign-in account is linked to another host',
  'ppl.ra.title': 'Visits waiting for reassignment',
  'ppl.ra.sub': 'Future visits whose primary host is no longer active.',
  'ppl.ra.none': 'No visits wait for reassignment.',
  'ppl.ra.host': 'Inactive host',
  'ppl.ra.reason': 'Why',
  'ppl.ra.reason.host_inactive_directory': 'Left the employee directory',
  'ppl.ra.reason.host_inactive_manual': 'Deactivated by an administrator',
  'ppl.ra.how': 'A delegated host at the site changes the primary host on the visit; the approval is requested again under the policy in force.',

  // ------------------------------------------------------------ import (PB-03-10)
  'ppl.imp.intro': 'Import hosts, visitor profiles and organisations from CSV or XLSX files.',
  'ppl.imp.title': 'Import records',
  'ppl.imp.sub': 'Nothing changes until you apply. Duplicates are never merged automatically, and password, token and other secret columns are never imported.',
  'ppl.imp.kind': 'Records',
  'ppl.imp.kind.people': 'Visitor profiles',
  'ppl.imp.kind.hosts': 'Hosts',
  'ppl.imp.kind.organisations': 'Organisations',
  'ppl.imp.file': 'File',
  'ppl.imp.fileHint': 'CSV (UTF-8) or XLSX, first row as headers. Formulas are not evaluated and macro-enabled workbooks are refused.',
  'ppl.imp.mapping': 'Column mapping',
  'ppl.imp.mappingHint': '{rows} rows found. Choose the field for each column; unmapped columns are not stored.',
  'ppl.imp.blocked': 'Never imported',
  'ppl.imp.column': 'Column',
  'ppl.imp.field': 'Field',
  'ppl.imp.fieldFor': 'Field for column {column}',
  'ppl.imp.ignore': 'Do not import',
  'ppl.imp.neverImported': 'Never imported',
  'ppl.imp.dryRun': 'Check the file (dry run)',
  'ppl.imp.history': 'Imports',
  'ppl.imp.none': 'No imports yet.',
  'ppl.imp.counts': 'Rows',
  'ppl.imp.countsLine': '{rows} rows · {valid} valid · {invalid} invalid · {duplicate} possible duplicates · {applied} applied · {skipped} skipped',
  'ppl.imp.open': 'Open',
  'ppl.imp.status.uploaded': 'Uploaded',
  'ppl.imp.status.validated': 'Checked — not applied',
  'ppl.imp.status.applying': 'Applying',
  'ppl.imp.status.applied': 'Applied',
  'ppl.imp.status.discarded': 'Discarded',
  'ppl.imp.report': 'Dry-run report · {file}',
  'ppl.imp.show': 'Show',
  'ppl.imp.row.all': 'All rows',
  'ppl.imp.row.pending': 'Pending',
  'ppl.imp.row.valid': 'Valid',
  'ppl.imp.row.invalid': 'Invalid',
  'ppl.imp.row.duplicate': 'Possible duplicate',
  'ppl.imp.row.applied': 'Applied',
  'ppl.imp.row.skipped': 'Skipped',
  'ppl.imp.ignored': 'Columns not imported',
  'ppl.imp.rows': 'Rows',
  'ppl.imp.rowNo': 'Row',
  'ppl.imp.values': 'Values',
  'ppl.imp.problems': 'Problems and possible matches',
  'ppl.imp.possible': 'Possible matches',
  'ppl.imp.useExisting': 'Use this record',
  'ppl.imp.updateExisting': 'Update this host',
  'ppl.imp.createSeparate': 'Create a separate record',
  'ppl.imp.skip': 'Skip row',
  'ppl.imp.decided': 'Decision: {decision}',
  'ppl.imp.decision.create': 'create separately',
  'ppl.imp.decision.use_existing': 'use the existing record',
  'ppl.imp.decision.update_existing': 'update the existing host',
  'ppl.imp.decision.skip': 'skip',
  'ppl.imp.policy': 'How to apply',
  'ppl.imp.policy.all_or_nothing': 'All rows or nothing',
  'ppl.imp.policy.valid_rows_only': 'Valid rows only',
  'ppl.imp.policy.all_or_nothing.hint': 'Applies every row in one step. Refused while any row is invalid or a duplicate is undecided.',
  'ppl.imp.policy.valid_rows_only.hint': 'Applies valid rows in batches; invalid and undecided rows are skipped and listed.',
  'ppl.imp.apply': 'Apply',
  'ppl.imp.discard': 'Discard',
  'ppl.imp.progress': '{applied} applied, {remaining} remaining…',

  // ------------------------------------------------------------ field dictionary (OR-07, OR-08)
  'ppl.dict.intro': 'Decide which extra details are kept for visitors and hosts, how they are protected and when they are asked for.',
  'ppl.dict.title': 'Field dictionary',
  'ppl.dict.published': 'Version {number} is in force.',
  'ppl.dict.draft': 'Draft version {number} is waiting to be published.',
  'ppl.dict.person': 'Visitor profile fields',
  'ppl.dict.personSub': 'A field is only asked for in the categories you choose; defining it does not collect it.',
  'ppl.dict.host': 'Host fields',
  'ppl.dict.hostSub': 'Extra details on host records.',
  'ppl.dict.addField': 'Add field',
  'ppl.dict.noFields': 'No fields.',
  'ppl.dict.removeField': 'Remove field {key}',
  'ppl.dict.key': 'Key',
  'ppl.dict.type': 'Type',
  'ppl.dict.classification': 'Protection',
  'ppl.dict.collect': 'Ask for it in',
  'ppl.dict.categories': 'Categories',
  'ppl.dict.categoriesHint': 'Category codes, separated by commas.',
  'ppl.dict.requiredFor': 'Required for categories',
  'ppl.dict.required': 'Required',
  'ppl.dict.options': 'Choices',
  'ppl.dict.optionsHint': 'One per line: value=English label|Arabic label',
  'ppl.dict.purposes': 'Reveal purposes',
  'ppl.dict.purposesSub': 'Every reveal of a masked value must name one of these purposes.',
  'ppl.dict.code': 'Code',
  'ppl.dict.addPurpose': 'Add purpose',
  'ppl.dict.revealSeconds': 'Show revealed values for (seconds)',
  'ppl.dict.saveDraft': 'Save draft',
  'ppl.dict.publish': 'Publish draft',
  'ppl.dict.discard': 'Discard draft',
} as const;
